import React from 'react';
import './CodeErrorPage.css';

interface CodeErrorPageProps {
  error: string;
  code?: string;
  onRetry?: () => void;
}

const CodeErrorPage: React.FC<CodeErrorPageProps> = ({ error, code, onRetry }) => {
  const getErrorDetails = () => {
    const lowerError = error.toLowerCase();
    
    if (lowerError.includes('deactivated')) {
      return {
        icon: '🔒',
        title: 'Access Code Deactivated',
        type: 'deactivated',
        description: 'This access code has been replaced with a newer one.',
        action: 'Contact your lawyer for the latest access code.'
      };
    } else if (lowerError.includes('expired')) {
      return {
        icon: '⏰',
        title: 'Access Code Expired',
        type: 'expired',
        description: 'This access code has passed its expiration date.',
        action: 'Contact your lawyer for a new access code.'
      };
    } else if (lowerError.includes('invalid')) {
      return {
        icon: '❌',
        title: 'Invalid Access Code',
        type: 'invalid',
        description: 'The access code you entered could not be found.',
        action: 'Please check the code and try again, or contact your lawyer.'
      };
    } else {
      return {
        icon: '⚠️',
        title: 'Access Error',
        type: 'unknown',
        description: 'There was a problem accessing your form.',
        action: 'Please contact your lawyer for assistance.'
      };
    }
  };

  const errorDetails = getErrorDetails();

  return (
    <div className="code-error-page">
      <div className="error-container">
        <div className="error-content">
          <div className="error-icon">
            {errorDetails.icon}
          </div>
          
          <h1 className="error-title">
            {errorDetails.title}
          </h1>
          
          <p className="error-description">
            {errorDetails.description}
          </p>
          
          {code && (
            <div className="error-code-info">
              <p><strong>Code used:</strong> <span className="code-display">{code}</span></p>
            </div>
          )}
          
          <div className="error-message-detail">
            <p className="error-detail">{error}</p>
          </div>
          
          <div className="error-actions">
            <div className="primary-action">
              <h3>What to do next:</h3>
              <p>{errorDetails.action}</p>
            </div>
            
            {onRetry && (
              <button 
                className="retry-btn"
                onClick={onRetry}
              >
                Try Different Code
              </button>
            )}
            
            <div className="help-info">
              <h4>Need Help?</h4>
              <ul>
                <li>
                  <strong>Deactivated code:</strong> Your lawyer generated a new code for security. 
                  The old code is no longer valid.
                </li>
                <li>
                  <strong>Expired code:</strong> Access codes are valid for a limited time (usually 15-30 days).
                </li>
                <li>
                  <strong>Invalid code:</strong> Double-check the 8-character code provided by your lawyer.
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="error-footer">
          <p className="security-note">
            🔐 <strong>Security Note:</strong> Access codes are automatically managed for your protection. 
            Only the most recent code for your form will work.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CodeErrorPage; 